import { Expose } from 'class-transformer';
import { Point } from 'geojson';

import { SnModel } from '@lib/common/models';

import { IFsLprResult } from '../interfaces/common/result';

export class LprQueryModel extends SnModel {
  @Expose({ name: 'query_id' })
  public id: string;

  @Expose()
  public items: LprQueryResultsModel[];

  @Expose()
  public size: number;

  @Expose({ name: 'next_token' })
  public nextToken: string;

  @Expose({ name: 'organization_id' })
  public organizationId: string;

  @Expose({ name: 'user_id' })
  public userId: string;

  @Expose({ name: 'user_name' })
  public userName: string;

  @Expose({ name: 'search_reason' })
  public searchReason: string;

  @Expose()
  public filters: any;

  @Expose()
  public expired: boolean;

  @Expose({ name: 'created_timestamp' })
  public createdTimeStamp: string;

  @Expose()
  public status: any;

  @Expose()
  public results: any;

  @Expose({ name: 'time_taken' })
  public timeTaken: any;

  @Expose()
  public count: number;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}

export class LprQueryResultsModel extends SnModel implements IFsLprResult {
  @Expose()
  public id: string;

  @Expose()
  public provider: string;

  @Expose({ name: 'organization_id' })
  public organizationId: number;

  @Expose({ name: 'camera_id' })
  public cameraId: string;

  @Expose({ name: 'camera_name' })
  public cameraName: string;

  @Expose({ name: 'created_timestamp' })
  public createdTimeStamp: string;

  @Expose({ name: 'event_timestamp' })
  public eventTimeStamp: string;

  @Expose()
  public plate: string;

  @Expose()
  public geometry: Point;

  @Expose({ name: 'alternate_plates' })
  public alternatePlate: string;

  @Expose()
  public media: any[];

  @Expose({ name: 'plate_confidence' })
  public plateConfidence: number;

  @Expose({ name: 'plate_state' })
  public plateState: string;

  @Expose({ name: 'plate_state_confidence' })
  public plateStateConfidence: number;

  @Expose({ name: 'vehicle_body_type' })
  public vehicleBodyType: string;

  @Expose({ name: 'vehicle_body_type_confidence' })
  public vehicleBodyTypeConfidence: number;

  @Expose({ name: 'vehicle_color' })
  public vehicleColor: string;

  @Expose({ name: 'vehicle_color_confidence' })
  public vehicleColorConfidence: number;

  @Expose({ name: 'vehicle_make' })
  public vehicleMake: string;

  @Expose({ name: 'vehicle_make_confidence' })
  public vehicleMakeConfidence: number;

  @Expose({ name: 'vehicle_model' })
  public vehicleModel: string;

  @Expose({ name: 'vehicle_model_confidence' })
  public vehicleModelConfidence: number;

  @Expose({ name: 'vehicle_year' })
  public vehicleYear: string;

  @Expose({ name: 'vehicle_year_confidence' })
  public vehicleYearConfidence: number;

  @Expose({ name: 'hits' })
  public hits: any[];

  @Expose({ name: 'reason' })
  public reason: any;

  public integratedLprType: string;

  get plateImageUrl(): string {
    return this.media.find((m: any) => m.type === 'plate')?.url || '';
  }

  /**
   * Get the context image URL
   * First try context or vehicle image, if not found, try any other non-plate image
   * @returns {string} The context image URL
   */
  get contextImageUrl(): string {
    return (
      this.media.find((m: any) => ['context', 'vehicle'].includes(m.type))?.url ||
      this.media.find((m: any) => m.type !== 'plate')?.url ||
      ''
    );
  }

  get vehicleBody(): string {
    return this.vehicleBodyType;
  }

  get type(): string {
    return this.isHit ? 'hit' : 'read';
  }

  get isHit(): boolean {
    return this.hits?.length > 0;
  }

  get hitReason(): string {
    return this.hits?.map((hit) => hit.reason).join(', ');
  }

  get state(): string {
    return this.plateState;
  }

  get displayState(): string {
    return this.plateState;
  }

  get createdTimestamp(): Date {
    return new Date(this.createdTimeStamp);
  }

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}
